import { localStorageUtils } from '@paneon/core';
import { createContext, useState, useRef, useCallback, useEffect, useContext } from 'react';
import { useAuth } from '@paneon/auth';
import { jsx } from 'react/jsx-runtime';

// src/localStorage.ts
function createLocalStorage(prefix = "paneon") {
  const getKey = (key) => `${prefix}:${key}`;
  return {
    async get(key) {
      return localStorageUtils.get(getKey(key));
    },
    async set(key, value) {
      localStorageUtils.set(getKey(key), value);
    },
    async remove(key) {
      localStorageUtils.remove(getKey(key));
    },
    async clear() {
      if (typeof window === "undefined") return;
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(`${prefix}:`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  };
}
function createSessionStorage(prefix = "paneon") {
  const getKey = (key) => `${prefix}:${key}`;
  return {
    async get(key) {
      if (typeof window === "undefined") return null;
      try {
        const item = sessionStorage.getItem(getKey(key));
        return item ? JSON.parse(item) : null;
      } catch {
        return null;
      }
    },
    async set(key, value) {
      if (typeof window === "undefined") return;
      try {
        sessionStorage.setItem(getKey(key), JSON.stringify(value));
      } catch {
        console.error("Failed to set session storage");
      }
    },
    async remove(key) {
      if (typeof window === "undefined") return;
      sessionStorage.removeItem(getKey(key));
    },
    async clear() {
      if (typeof window === "undefined") return;
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(`${prefix}:`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }
  };
}

// src/apiStorage.ts
function createApiStorage(config) {
  const { baseUrl, getAuthToken, onError } = config;
  async function makeRequest(method, endpoint, body) {
    try {
      const token = await getAuthToken?.();
      const headers = {
        "Content-Type": "application/json"
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : void 0
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Unknown API error");
      }
      return data.data ?? null;
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }
  return {
    async get(key) {
      return makeRequest("GET", `/storage/${encodeURIComponent(key)}`);
    },
    async set(key, value) {
      await makeRequest("PUT", `/storage/${encodeURIComponent(key)}`, { value });
    },
    async remove(key) {
      await makeRequest("DELETE", `/storage/${encodeURIComponent(key)}`);
    },
    async clear() {
      await makeRequest("DELETE", "/storage");
    }
  };
}
var StorageContext = createContext(null);
var DEFAULT_PREFERENCES = {
  userId: "",
  themeId: "paneon",
  notificationEnabled: true,
  language: "de",
  dashboardWidgetsV2: void 0,
  widgetOrder: [],
  autoCompactGrid: false,
  createdAt: Date.now() / 1e3
};
function StorageProvider({
  children,
  apiConfig,
  cachePrefix = "paneon"
}) {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const localStorageRef = useRef(createLocalStorage(cachePrefix));
  const apiStorageRef = useRef(
    apiConfig ? createApiStorage(apiConfig) : null
  );
  const loadPreferences = useCallback(async (currentUser) => {
    setIsLoading(true);
    setError(null);
    try {
      if (apiStorageRef.current && currentUser) {
        const apiPrefs = await apiStorageRef.current.get(`preferences:${currentUser.id}`);
        if (apiPrefs) {
          setPreferences(apiPrefs);
          await localStorageRef.current.set(`preferences:${currentUser.id}`, apiPrefs);
          setIsLoading(false);
          return;
        }
      }
      if (currentUser) {
        const localPrefs = await localStorageRef.current.get(`preferences:${currentUser.id}`);
        if (localPrefs) {
          setPreferences(localPrefs);
          setIsLoading(false);
          return;
        }
      }
      if (currentUser) {
        const defaultPrefs = {
          ...DEFAULT_PREFERENCES,
          userId: currentUser.id
        };
        setPreferences(defaultPrefs);
      } else {
        setPreferences(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load preferences");
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    loadPreferences(user);
  }, [user, loadPreferences]);
  const updatePreferences = useCallback(
    async (updates) => {
      if (!preferences || !user) return;
      const newPreferences = {
        ...preferences,
        ...updates
      };
      setPreferences(newPreferences);
      await localStorageRef.current.set(`preferences:${user.id}`, newPreferences);
      if (apiStorageRef.current) {
        try {
          await apiStorageRef.current.set(`preferences:${user.id}`, newPreferences);
        } catch (err) {
          console.error("Failed to sync preferences to API:", err);
        }
      }
    },
    [preferences, user]
  );
  const get = useCallback(async (key) => {
    if (apiStorageRef.current) {
      const value2 = await apiStorageRef.current.get(key);
      if (value2 !== null) return value2;
    }
    return await localStorageRef.current.get(key);
  }, []);
  const set = useCallback(async (key, value2) => {
    await localStorageRef.current.set(key, value2);
    if (apiStorageRef.current) {
      await apiStorageRef.current.set(key, value2);
    }
  }, []);
  const remove = useCallback(async (key) => {
    await localStorageRef.current.remove(key);
    if (apiStorageRef.current) {
      await apiStorageRef.current.remove(key);
    }
  }, []);
  const sync = useCallback(async () => {
    if (!apiStorageRef.current || !user || !preferences) return;
    try {
      setIsLoading(true);
      await apiStorageRef.current.set(`preferences:${user.id}`, preferences);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setIsLoading(false);
    }
  }, [user, preferences]);
  const value = {
    preferences,
    isLoading,
    error,
    updatePreferences,
    get,
    set,
    remove,
    sync
  };
  return /* @__PURE__ */ jsx(StorageContext.Provider, { value, children });
}
function useStorage() {
  const context = useContext(StorageContext);
  if (!context) {
    throw new Error("useStorage must be used within a StorageProvider");
  }
  return context;
}
function usePreferences() {
  const { preferences, updatePreferences, isLoading } = useStorage();
  return { preferences, updatePreferences, isLoading };
}

export { StorageProvider, createApiStorage, createLocalStorage, createSessionStorage, usePreferences, useStorage };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map